import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import type {
  SupportTicketDetailResponse,
  SupportTicketListItem,
  SupportTicketSummary,
  SupportTicketSortByValue,
  TicketStatusBackend,
} from "@/features/support-tickets";

const CLIENT_SUPPORT_TICKETS_ENDPOINT = "/api/client/support-tickets";

export interface ClientSupportTicketListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: TicketStatusBackend;
  /** Sadece bu eğitime bağlı talepleri getir (UUID). */
  educationId?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: SupportTicketSortByValue;
}

export interface CreateClientSupportTicketDto {
  title: string;
  description: string;
  /** Optional single file attachment (first file only) */
  fileIds?: string[];
  /** Talep belirli bir eğitime bağlanacaksa eğitimin UUID'si */
  educationId?: string;
}

export const clientSupportTicketsApi = {
  /**
   * GET /api/client/support-tickets - List my tickets with pagination and filters.
   */
  getAll: async (
    params?: ClientSupportTicketListParams
  ): Promise<{ data: SupportTicketListItem[]; total: number }> => {
    const query: Record<string, unknown> = {};
    if (params?.page != null) query.page = params.page;
    if (params?.limit != null) query.limit = params.limit;
    if (params?.search) query.search = params.search;
    if (params?.status != null) query.status = params.status;
    if (params?.educationId) query.educationId = params.educationId;
    if (params?.dateFrom) query.dateFrom = params.dateFrom;
    if (params?.dateTo) query.dateTo = params.dateTo;
    if (params?.sortBy) query.sortBy = params.sortBy;

    const { data } = await apiClient.get<{
      success?: boolean;
      status?: number;
      items?: SupportTicketListItem[];
      data?: SupportTicketListItem[];
      count?: number;
      message?: string;
    }>(CLIENT_SUPPORT_TICKETS_ENDPOINT, { params: query });

    const list = data.items ?? data.data ?? [];
    return {
      data: list,
      total: data.count ?? list.length,
    };
  },

  /**
   * GET /api/client/support-tickets/summary
   * Get summary of my support tickets for info cards.
   */
  getSummary: async (): Promise<SupportTicketSummary> => {
    const { data } = await apiClient.get<{
      success?: boolean;
      status?: number;
      data?: SupportTicketSummary;
      message?: string;
    }>(`${CLIENT_SUPPORT_TICKETS_ENDPOINT}/summary`);

    if (!data?.data) {
      return {
        totalCount: 0,
        pendingCount: 0,
        resolvedCount: 0,
      };
    }
    return data.data;
  },

  /**
   * GET /api/client/support-tickets/{id}
   * Get my support ticket details by ID.
   */
  getDetail: async (id: string): Promise<SupportTicketDetailResponse> => {
    const { data } = await apiClient.get<{
      success?: boolean;
      status?: number;
      data: SupportTicketDetailResponse;
      message?: string;
    }>(`${CLIENT_SUPPORT_TICKETS_ENDPOINT}/${id}`);
    if (!data?.data) {
      throw new Error(i18n.t("clientSupportTicketsApi.notFound"));
    }
    return data.data;
  },

  /**
   * POST /api/client/support-tickets
   * Create a new support ticket for the current user.
   */
  create: async (body: CreateClientSupportTicketDto): Promise<void> => {
    await apiClient.post(CLIENT_SUPPORT_TICKETS_ENDPOINT, body);
  },

  /**
   * POST /api/client/support-tickets/{id}/messages
   * Kendi talebime mesaj ekler (karşılıklı konuşma; tekrar tekrar yazılabilir).
   * Body: content ve/veya fileId (en az biri zorunlu). Çözülen talep kilitlidir => 400.
   */
  postMessage: async (
    id: string,
    body: { content?: string; fileId?: string }
  ): Promise<void> => {
    if (!body.content?.trim() && !body.fileId) {
      throw new Error(i18n.t("clientSupportTicketsApi.contentOrFileRequired"));
    }
    await apiClient.post(`${CLIENT_SUPPORT_TICKETS_ENDPOINT}/${id}/messages`, body);
  },
};

